<script setup>
import { computed, inject } from 'vue';
import SubHead from '@/components/result/MLSRD-2026/SubHead.vue';
import AchievementTable from '@/components/result/MLSRD-2026/AchievementTable.vue'; 
import ScoreAchievement from '@/components/result/MLSRD-2026/ScoreAchievement.vue';

// 부모에서 주입받은 데이터
const studentData = inject('studentData');
const loading = inject('loading');

const pagetype = "literacy";
const region = "seoul";

// 학교급과 학년에 따른 레벨 구성 및 점수 범위
const getLevelConfig = (schulType, gradeCode) => {
    const key = `${schulType}${gradeCode}`;
    
    const configs = {
        'E4': { // 초4
            rangeMin: 1000, rangeMax: 1700,
            levels: [
                { level: 1, range: '1000~1226' },
                { level: 2, range: '1227~1353' },
                { level: 3, range: '1354~1526' },
                { level: 4, range: '1527~1700' }
            ]
        },
        'E6': { // 초6  
            rangeMin: 1050, rangeMax: 1800,
            levels: [
                { level: 1, range: '1050~1320' },
                { level: 2, range: '1321~1432' },
                { level: 3, range: '1433~1594' },
                { level: 4, range: '1595~1800' }
            ]
        },
        'M2': { // 중2
            rangeMin: 1100, rangeMax: 1900,
            levels: [
                { level: 1, range: '1100~1412' },
                { level: 2, range: '1413~1529' },
                { level: 3, range: '1530~1665' },
                { level: 4, range: '1666~1900' }
            ]
        },
        'H1': { // 고1
            rangeMin: 1150, rangeMax: 2000,
            levels: [
                { level: 1, range: '1150~1534' },
                { level: 2, range: '1535~1622' },
                { level: 3, range: '1623~1739' },
                { level: 4, range: '1740~2000' }
            ]
        }
    };
    
    return configs[key] || configs['E4']; // 기본값은 초4
};

// 학년 텍스트 생성
const getGradeText = (schulType, gradeCode) => {
    const typeMap = { 'E': '초등학교', 'M': '중학교', 'H': '고등학교' };
    return `${typeMap[schulType] || ''} ${gradeCode}`;
};

// 학교급 간단 텍스트 생성
const getShortGradeText = (schulType, gradeCode) => {
    const typeMap = { 'E': '초', 'M': '중', 'H': '고' };
    return `${typeMap[schulType] || ''}${gradeCode}`;
};

// 학년만 텍스트 생성
const getGradeOnlyText = (gradeCode) => {
    return `${gradeCode}학년`;
};


// computed 데이터들
const levelConfig = computed(() => {
    if (!studentData.value) return getLevelConfig('E', '4');
    return getLevelConfig(studentData.value.schul_type, studentData.value.grade_code);
});

const score = computed(() => {
    if (!studentData.value) {
        return { rangeMin: 1000, rangeMax: 1700, average: 1453 };
    }
    
    const config = getLevelConfig(studentData.value.schul_type, studentData.value.grade_code);
    return {
        rangeMin: config.rangeMin,
        rangeMax: config.rangeMax,
        average: parseInt(studentData.value.korscore_avg_class) || config.rangeMin
    };
});

const student = computed(() => {
    if (!studentData.value) {
        return { name: '로딩중...', grade: 4, score: 1531 };
    }
    
    const gradeText = getGradeText(studentData.value.schul_type, studentData.value.grade_code);
    return {
        name: studentData.value.name || '',
        grade: gradeText,
        score: studentData.value.korscore || 0
    };
});

const levelYear = computed(() => {
    if (!studentData.value) return '초등학교 4학년';
    return `${getGradeText(studentData.value.schul_type, studentData.value.grade_code)}`;
});
const shortLevelYear = computed(() => {
    if (!studentData.value) return '초4';
    return `${getShortGradeText(studentData.value.schul_type, studentData.value.grade_code)}`;
});

const gradeOnlyYear = computed(() => {
    if (!studentData.value) return '4학년';
    return `${getGradeOnlyText(studentData.value.grade_code)}`;
});
</script>

<template>
    <div class="slide-inner">
        <div class="content">            
            <SubHead :type="pagetype" :region="region" title="문해력 점수와 수준"/>             
            <ScoreAchievement
                :pagetype="pagetype"
                :region="region"
                :studentName="student.name"
                :grade="student.grade"
                :scoreRangeMin="score.rangeMin"
                :scoreRangeMax="score.rangeMax"
                :studentScore="student.score"
                :averageScore="score.average"
                :levels="levelConfig.levels"
            />
            <h5 class="card-title literacy">각 수준에서 기대되는 성취 수행 : <span :class="`${pagetype}-color pl-[5px]`"> 문해력 {{ gradeOnlyYear }} </span></h5>
            <AchievementTable :type="pagetype" :levelYear="shortLevelYear" :isStudent="true" />     
            <p class="caution pt-4">각 성취 수준에 있는 기술은 해당 수준에 있는 학생이 ‘확률적’으로 수행할 것으로 기대되는 내용을 제시한 것이며, 각 학생이 실제로 성취 수준에 있는 내용을 수행했다는 것을 의미하지는 않습니다.</p> 
        </div>
    </div>
</template>
